import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = 'https://budget-app-server-two.vercel.app/api';

  const api = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = 'Bearer ' + token;
    return config;
  });

  // Check for existing token on load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload && payload.id) {
            setUser({
              id: payload.id,
              email: payload.email,
              role: payload.role,
              username: payload.email?.split('@')[0] || 'User',
              subscriptionStatus: 'active'
            });
          }
        } catch (e) {
          console.error('Token decode failed:', e);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const register = async (username, email, password, currency = 'USD', phone = '') => {
    try {
      // CLEAR any existing token first
      localStorage.removeItem('token');
      
      const response = await api.post('/auth/register', { username, email, password, currency, phone });
      const { token, user: userData, message } = response.data;
      
      // Store NEW token
      localStorage.setItem('token', token);
      setUser(userData);
      
      toast.success(message || 'Account created!');
      return { success: true, user: userData };
    } catch (error) {
      const msg = error.response?.data?.error || 'Registration failed';
      toast.error(msg);
      return { success: false, error: msg };
    }
  };

  const login = async (email, password) => {
    try {
      // CLEAR any existing token first
      localStorage.removeItem('token');
      
      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;
      
      // Store NEW token
      localStorage.setItem('token', token);
      setUser(userData);
      
      toast.success('Login successful!');
      return { success: true, user: userData };
    } catch (error) {
      const msg = error.response?.data?.error || 'Login failed';
      toast.error(msg);
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    // Force clear any cached data
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};